import React, { Suspense } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import * as Sentry from '@sentry/browser'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { AppLayout } from '@fs/zion-ui'
import { Router, useLocation } from '@fs/zion-router'
import { appPath, sentryDSN, mergeSentryConfig } from '@fs/zion-config'
import { setAppLocales, essentialLocales } from '@fs/zion-locale'
import { BrightspotProvider } from '@fs/zion-cms'
import { NoticeLoading } from '@fs/zion-icon'

import App from './App'
import './locales'
import * as serviceWorker from './serviceWorker'
import useAlertBanner from './components/hooks/useAlertBanner'
import AlertBannerContext from './components/hooks/AlertBannerContext'
import { shouldHideHelpChatbot } from './components/shared/helpChatbotVisibility'

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities =
  process.env.NODE_ENV === 'development' ? import('./dev.activities').then((m) => m.default) : undefined

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to mergeSentryConfig
  Sentry.init(
    mergeSentryConfig({
      dsn: sentryDSN,
    })
  )
}

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

function RootWithLocation({ alertBannerProps }) {
  const { pathname } = useLocation()
  const hideHelpChatbot = shouldHideHelpChatbot(pathname)

  return (
    <Root
      analytics
      helpChatbot={!hideHelpChatbot}
      helperModeProps={{ redirectAfterStopUrl: '/help/helper' }}
      alertBannerProps={alertBannerProps}
    >
      <AlertBannerContext.Provider value={alertBannerProps}>
        <AppLayout fullWidth>
          <App />
        </AppLayout>
      </AlertBannerContext.Provider>
    </Root>
  )
}

// add any app logic here, languages and translations are configured and available here
const FrontierWrapper = () => {
  setAppLocales(essentialLocales)
  const alertBannerProps = useAlertBanner()

  return (
    <BrightspotProvider sites={['helpcenter']}>
      <Router basename={base}>
        <RootWithLocation alertBannerProps={alertBannerProps} />
      </Router>
    </BrightspotProvider>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = () => (
  <React.StrictMode>
    <Suspense fallback={<NoticeLoading />}>
      <FrontierBaseProviders mockFlags={mockFlags} mockActivities={mockActivities}>
        <FrontierWrapper />
      </FrontierBaseProviders>
    </Suspense>
  </React.StrictMode>
)

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
